import { format } from 'date-fns'
import i18next, { t } from 'i18next'
import { enGB, es as esESP } from 'date-fns/locale'
import MD5 from 'crypto-js/md5'
import { Country } from 'types/model/common'
import { Delivery } from 'types/api/delivery'
import * as yup from 'yup'
import { Product } from 'types/model/product'
import { routePlanningColors } from './theme'
import i18n from './i18'
import { contentLanguages } from './languages'

/**
 *
 * Converting int value to alphabets,
 * like 0=A,1=B
 * If its run out from alphabets, will consider 26=AA, 27=AB
 * Implementation is based on ASCII values -> for e.g. 65 = A(0+65)
 * @return String that consist of the alphabets - int value - representation
 */
export const getRouteNameInAlphabet = (groupIndex: number) => {
  const chars = []
  let i = groupIndex

  chars.push(String.fromCharCode((i % 26) + 65))
  i = (i - (i % 26)) / 26

  if (i > 0) {
    if (i % 26 === 0) {
      chars.push('Z')
      i -= 1
    } else {
      chars.push(String.fromCharCode(Math.floor((i % 26) + 64)))
    }
    i /= 26
  }
  return chars.reverse().join('')
}

export const selectDeliveryAreaColor = (index: number) => {
  const colorsNumber = routePlanningColors.length
  const delta = Math.floor(index / colorsNumber)
  const val = index - colorsNumber * delta
  return routePlanningColors[val]
}

export const formatDate = (date: Date) => {
  const dateLocales = { en: enGB, es: esESP }
  const options = {
    locale: dateLocales[i18next.language as keyof typeof dateLocales],
    weekStartsOn: 1 as const,
  }
  return format(date, 'P', options)
}

// TODO: this needs to be rewritten
export const generateCrc = (deliveryIds: number[], deliveries?: Delivery[]) => {
  if (deliveries && deliveries.length > 0) {
    return deliveries
      .map((delivery) => {
        if (deliveryIds.includes(delivery.id)) {
          return { crc: delivery.crc, deliveryId: delivery.id }
        }
        return null
      })
      .filter(Boolean)
  }
  return deliveryIds.map((id) => ({
    crc: MD5(id.toString()).toString(),
    deliveryId: id,
  }))
}

export const getCountries = (countries: Country[]) => {
  return countries
    .map((country: Country) => {
      return {
        label: Object.keys(country.name).includes(i18next.language)
          ? country.name[i18next.language]
          : Object.keys(country.name)[0],
        value: country.code,
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const isValidCoordinate = (
  num: string,
  type: 'longitude' | 'latitude'
) => {
  if (type === 'longitude') {
    return Boolean(
      Math.abs(parseFloat(num)) <= 180 && num.match(/^[+-]?\d+(\.\d+)?$/)
    )
  }
  return Boolean(
    Math.abs(parseFloat(num as string)) <= 90 && num.match(/^[+-]?\d+(\.\d+)?$/)
  )
}

export const CoordinatesSchema = yup
  .string()
  .required(t('required'))
  .test('coordinateFormat', (coordinates, ctx) => {
    const arrayOfCoordinates = coordinates?.trim().split('\n')
    if (
      (arrayOfCoordinates && arrayOfCoordinates.length < 3) ||
      !arrayOfCoordinates
    ) {
      return ctx.createError({
        message: t('serviceArea.atLeastThreeCoordinates'),
      })
    }
    const check = arrayOfCoordinates?.map((p) => {
      const parts = p.split(',')
      return Boolean(
        isValidCoordinate(parts[0].trim(), 'latitude') &&
          isValidCoordinate(parts[1].trim(), 'longitude')
      )
    })
    if (
      check?.includes(false) ||
      (coordinates && /[a-zA-Z]/.test(coordinates))
    ) {
      return ctx.createError({
        message: t('serviceArea.enterValidCoordinates'),
      })
    }
    return true
  })

export const SlotSchema = yup.object().shape(
  {
    date: yup.date().required(t('required')),
    startTime: yup.date().required(t('required')),
    endTime: yup
      .date()
      .required(t('required'))
      .when('startTime', (startTime, schema) => {
        return schema.min(
          startTime,
          t('mustBeLaterThan', { time: 'startTime' })
        )
      }),
    orderQuota: yup
      .number()
      .integer()
      .typeError(t('specifyNumber'))
      .min(0, t('minValueZero')),
    graceHours: yup
      .number()
      .typeError(t('specifyNumber'))
      .min(0, t('minValueZero')),
    feeProducts: yup.string().when('deliveryFee', {
      is: (deliveryFee: number) => !deliveryFee && deliveryFee !== 0,
      then: yup.string().required(t('required')),
    }),
    deliveryFee: yup
      .number()
      .typeError(t('specifyNumber'))
      .min(0, t('minValueZero'))
      .when('feeProducts', {
        is: (feeProducts: string) => !feeProducts || feeProducts.length === 0,
        then: yup
          .number()
          .typeError(t('specifyNumber'))
          .required(t('required')),
      }),
    visibilityStart: yup.string().nullable(true),
    visibilityEnd: yup.string().nullable(true),
    active: yup.boolean().required(t('required')),
    recurrent: yup.boolean(),
    validityStart: yup.date().required(t('required')),
    validityEnd: yup
      .date()
      .nullable(true)
      .when('recurrent', {
        is: true,
        then: yup.date().required(t('required')),
      }),
  },
  [['feeProducts', 'deliveryFee']]
)

export const selectLanguageInCodeList = (
  code: { '@languageCode': string; $: string }[]
) => {
  return code.filter((name) => name['@languageCode'] === i18n.language)
    ?.length > 0
    ? code.filter((name) => name['@languageCode'] === i18n.language)[0]
    : code.filter((name) => name['@languageCode'] === contentLanguages[0])[0]
}

// typescript has issues with .filter(Boolean)
// so instead of that we can use .filter(isNotBool) to bypass that issue
export function isNotBool<T>(argument: T | boolean): argument is T {
  return typeof argument !== 'boolean'
}

export const getPlaceOptions = (places: { label: string; value: string }[]) => {
  if (!places || places.length < 1) {
    return []
  }
  const firstFivePlaces = places.length > 5 ? places.slice(0, 5) : places

  return firstFivePlaces.map((place) => {
    return {
      key: 'place',
      placeId: place.value,
      placeName: place.label,
      label: t('searchContent.place'),
    }
  })
}

export const formatMultiLanguageField = (
  arr: { $: string; '@languageCode': string }[] | undefined
) => {
  if (!arr || arr.length === 0) {
    return contentLanguages.reduce(
      (acc: { [lang: string]: string }, lang: string) => {
        acc[lang] = ''
        return acc
      },
      {}
    )
  }
  const formattedData = arr.reduce(
    (
      acc: { [x: string]: string },
      name: { $: string; '@languageCode': string }
    ) => {
      acc[name['@languageCode']] = name.$
      return acc
    },
    {}
  )

  return contentLanguages.reduce(
    (acc: { [lang: string]: string }, lang: string) => {
      if (lang in formattedData) {
        acc[lang] = formattedData[lang]
      } else {
        acc[lang] = ''
      }
      return acc
    },
    {}
  )
}

export const formatPrice = (number: number) => {
  return new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 2,
  }).format(number)
}

export const productInfoForRefunds = (product: Product) => {
  const hasRefunds = product.refunds?.length > 0
  const refundQuantity = product.refunds?.reduce(
    (acc, r) => r.quantity + acc,
    0
  )
  const charged = product.collectedProduct.capturedPrice?.priceVat

  // if refunds have been made without quantity, leave quantity field blank
  if (hasRefunds) {
    return {
      charged,
      quantity:
        refundQuantity > 0
          ? product.collectedProduct.quantity - refundQuantity
          : '',
    }
  }
  return {
    charged,
    quantity: product.collectedProduct.quantity,
  }
}
